import type { SelectionBoundary, SelectionInfo } from '@aline/common';
import { isParagraphElement } from '@aline/common';
import { Editor, Element, Node, Path, Range, Text } from 'slate';

/**
 * Extracts selection information from a Slate editor selection.
 *
 * This function walks the selection range to determine:
 * 1. Which paragraph locationIds are selected
 * 2. Start boundary (if selection starts mid-paragraph)
 * 3. End boundary (if selection ends mid-paragraph)
 *
 * @param editor - The Slate editor
 * @param selection - The Slate selection range
 * @returns SelectionInfo for use with serializeSelectionContext
 */
export function getSelectionInfoFromRange(editor: Editor, selection: Range): SelectionInfo {
  // 1. Normalize selection to forward direction (anchor before focus)
  const [start, end] = Range.edges(selection);

  // 2. Get all paragraph nodes within the selection range
  const paragraphEntries = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: n => Element.isElement(n) && isParagraphElement(n),
    })
  );

  if (paragraphEntries.length === 0) {
    return { selectedLocationIds: [] };
  }

  // 3. For each paragraph, extract its locationId
  const selectedLocationIds: string[] = [];
  for (const [paragraphNode] of paragraphEntries) {
    if (isParagraphElement(paragraphNode) && paragraphNode.locationId) {
      selectedLocationIds.push(paragraphNode.locationId);
    }
  }

  // Remove duplicates while preserving order
  const uniqueLocationIds = [...new Set(selectedLocationIds)];

  // 4. Calculate start and end boundaries for partial selections
  const [firstParagraphNode, firstParagraphPath] = paragraphEntries[0];
  const [lastParagraphNode, lastParagraphPath] = paragraphEntries[paragraphEntries.length - 1];

  let startBoundary: SelectionBoundary | undefined;
  let endBoundary: SelectionBoundary | undefined;

  // Check if start is mid-paragraph
  if (
    isParagraphElement(firstParagraphNode) &&
    firstParagraphNode.locationId &&
    !isAtParagraphBoundary(editor, firstParagraphPath, start.path, start.offset, 'start')
  ) {
    const characterOffset = getCharacterOffsetInParagraph(
      editor,
      firstParagraphPath,
      start.path,
      start.offset
    );
    startBoundary = {
      characterOffset,
      paragraphLocationAnchor: firstParagraphNode.locationId,
    };
  }

  // Check if end is mid-paragraph
  if (
    isParagraphElement(lastParagraphNode) &&
    lastParagraphNode.locationId &&
    !isAtParagraphBoundary(editor, lastParagraphPath, end.path, end.offset, 'end')
  ) {
    const characterOffset = getCharacterOffsetInParagraph(
      editor,
      lastParagraphPath,
      end.path,
      end.offset
    );
    endBoundary = {
      characterOffset,
      paragraphLocationAnchor: lastParagraphNode.locationId,
    };
  }

  return {
    endBoundary,
    selectedLocationIds: uniqueLocationIds,
    startBoundary,
  };
}

/**
 * Calculates the character offset from the start of a paragraph to a given point.
 * Accounts for multiple text nodes and inline elements.
 *
 * @param editor - The Slate editor
 * @param paragraphPath - Path to the paragraph node
 * @param pointPath - Path to the point's text node
 * @param pointOffset - Character offset within the point's text node
 * @returns Total character offset from paragraph start
 */
export function getCharacterOffsetInParagraph(
  editor: Editor,
  paragraphPath: Path,
  pointPath: Path,
  pointOffset: number
): number {
  const paragraphNode = Node.get(editor, paragraphPath);
  if (!Element.isElement(paragraphNode)) return 0;

  let totalOffset = 0;

  // Walk all text nodes in the paragraph until we reach the point
  for (const [node, nodePath] of Node.texts(paragraphNode)) {
    // Calculate the full path in the editor (paragraph path + relative path)
    const fullPath = [...paragraphPath, ...nodePath];

    // Check if this text node is before the point
    const comparison = Path.compare(fullPath, pointPath);

    if (comparison < 0) {
      // This text node is entirely before the point - add its full length
      totalOffset += node.text.length;
    } else if (comparison === 0) {
      // This is the text node containing the point - add the offset within it
      totalOffset += pointOffset;
      break;
    }
    // If comparison > 0, we've gone past the point (shouldn't happen)
  }

  return totalOffset;
}

/**
 * Determines if a selection point is at the very start or end of a paragraph.
 * Used to determine if we need partial paragraph splitting.
 *
 * @param editor - The Slate editor
 * @param paragraphPath - Path to the paragraph node
 * @param pointPath - Path to the point's text node
 * @param pointOffset - Character offset within the point's text node
 * @param boundary - Whether to check 'start' or 'end' boundary
 * @returns true if the point is at the specified boundary of the paragraph
 */
export function isAtParagraphBoundary(
  editor: Editor,
  paragraphPath: Path,
  pointPath: Path,
  pointOffset: number,
  boundary: 'start' | 'end'
): boolean {
  const paragraphNode = Node.get(editor, paragraphPath);
  if (!Element.isElement(paragraphNode)) return false;

  if (boundary === 'start') {
    // Check if point is at the very start of the paragraph
    // This means the point's offset is 0 and it's in the first text node

    if (pointOffset !== 0) return false;

    // Get the first text node in the paragraph
    const firstTextEntry = Editor.first(editor, paragraphPath);
    if (!firstTextEntry) return true; // Empty paragraph

    const [, firstTextPath] = firstTextEntry;
    return Path.equals(pointPath, firstTextPath);
  }
  // Check if point is at the very end of the paragraph
  // This means the point is at the end of the last text node

  // Get the last text node in the paragraph
  const lastTextEntry = Editor.last(editor, paragraphPath);
  if (!lastTextEntry) return true; // Empty paragraph

  const [lastTextNode, lastTextPath] = lastTextEntry;

  if (!Path.equals(pointPath, lastTextPath)) return false;

  // Check if offset is at the end of the text
  if (Text.isText(lastTextNode)) {
    return pointOffset === lastTextNode.text.length;
  }

  return false;
}
